import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Home, Building2, Heart, LogOut, UserCircle2, PlusCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/locanest/NotificationBell";

export default function Navbar() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const linkBase = "px-3 py-2 text-sm font-medium transition-colors hover:text-[#C85A32]";
  const active = ({ isActive }) => `${linkBase} ${isActive ? "text-[#C85A32]" : "text-[#1A1F2C]"}`;

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FDFBF7]/80 border-b border-[#1A1F2C]/10">
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-[#C85A32] grid place-items-center text-[#FDFBF7]">
            <Home size={18} />
          </div>
          <div className="leading-none">
            <div className="font-display text-xl text-[#1A1F2C]">LocaNest</div>
            <div className="hidden min-[400px]:block whitespace-nowrap text-[10px] uppercase tracking-[0.25em] text-[#5C6B7A]">Your Verified Rental Partner</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-1" data-testid="navbar-desktop">
          <NavLink to="/" end className={active} data-testid="nav-home">Home</NavLink>
          <NavLink to="/listings" className={active} data-testid="nav-listings">Listings</NavLink>
          <NavLink to="/vendors" className={active} data-testid="nav-vendors">Top Vendors</NavLink>
          <NavLink to="/map" className={active} data-testid="nav-map">Map</NavLink>
          <NavLink to="/support" className={active} data-testid="nav-support">Help</NavLink>
          {user?.role === "owner" && (
            <NavLink to="/owner" className={active} data-testid="nav-owner">My Properties</NavLink>
          )}
          {user?.role === "owner" && (
            <NavLink to="/owner/verify" className={active} data-testid="nav-verify">Verify</NavLink>
          )}
          {user?.role === "admin" && (
            <NavLink to="/admin" className={active} data-testid="nav-admin">Admin</NavLink>
          )}
          {user?.role === "tenant" && (
            <NavLink to="/favorites" className={active} data-testid="nav-favorites">Favorites</NavLink>
          )}
          {user?.role === "tenant" && (
            <NavLink to="/bookings" className={active} data-testid="nav-bookings">Bookings</NavLink>
          )}
        </nav>

        <div className="flex items-center gap-1">
        {user && <NotificationBell />}
        <div className="hidden md:flex items-center gap-2">
          {user?.role === "owner" && (
            <Button asChild className="bg-[#2B3A67] hover:bg-[#1f2c52] text-white rounded-md" data-testid="cta-add-property">
              <Link to="/owner/new"><PlusCircle size={16} className="mr-1.5" /> List Property</Link>
            </Button>
          )}
          {!user ? (
            <>
              <Button asChild variant="ghost" className="text-[#1A1F2C]" data-testid="nav-login">
                <Link to="/login">Login</Link>
              </Button>
              <Button asChild className="bg-[#C85A32] hover:bg-[#a8482a] text-white rounded-md" data-testid="nav-register">
                <Link to="/register">Sign up</Link>
              </Button>
            </>
          ) : (
            <div className="flex items-center gap-2">
              <Link to="/profile" className="flex items-center gap-1.5 text-sm text-[#1A1F2C] hover:text-[#C85A32] px-2 py-1 rounded-md" data-testid="nav-profile">
                {user.avatar ? <img src={user.avatar} alt="" className="w-7 h-7 rounded-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <UserCircle2 size={22} />}
                <span className="hidden lg:inline" data-testid="user-name">{user.name.split(" ")[0]}</span>
              </Link>
              <Button variant="outline" size="sm" onClick={doLogout} className="rounded-md" data-testid="nav-logout">
                <LogOut size={14} className="mr-1" /> Logout
              </Button>
            </div>
          )}
        </div>

        <div className="md:hidden flex items-center gap-1">
          {user && (
            <Link to="/profile" className="p-2 text-[#1A1F2C]" aria-label="My profile" data-testid="mobile-nav-profile-icon">
              {user.avatar ? <img src={user.avatar} alt="" className="w-7 h-7 rounded-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} /> : <UserCircle2 size={22} />}
            </Link>
          )}
          <button className="p-2" onClick={() => setOpen((v) => !v)} aria-label="menu" data-testid="mobile-menu-toggle">
            {open ? <X /> : <Menu />}
          </button>
        </div>
        </div>
      </div>

      {open && (
        <div className="md:hidden border-t border-[#1A1F2C]/10 bg-[#FDFBF7]" data-testid="mobile-menu">
          <div className="px-5 py-4 flex flex-col gap-1">
            <NavLink to="/" end onClick={() => setOpen(false)} className={active} data-testid="mobile-nav-home">Home</NavLink>
            <NavLink to="/listings" onClick={() => setOpen(false)} className={active} data-testid="mobile-nav-listings">Listings</NavLink>
            <NavLink to="/vendors" onClick={() => setOpen(false)} className={active} data-testid="mobile-nav-vendors">Top Vendors</NavLink>
            <NavLink to="/map" onClick={() => setOpen(false)} className={active} data-testid="mobile-nav-map">Map</NavLink>
            <NavLink to="/support" onClick={() => setOpen(false)} className={active} data-testid="mobile-nav-support">Help &amp; Support</NavLink>
            {user?.role === "owner" && <NavLink to="/owner" onClick={() => setOpen(false)} className={active}>My Properties</NavLink>}
            {user?.role === "owner" && <NavLink to="/owner/new" onClick={() => setOpen(false)} className={active}>List Property</NavLink>}
            {user?.role === "tenant" && <NavLink to="/favorites" onClick={() => setOpen(false)} className={active}>Favorites</NavLink>}
            {user?.role === "tenant" && <NavLink to="/bookings" onClick={() => setOpen(false)} className={active}>My Bookings</NavLink>}
            {user && <NavLink to="/profile" onClick={() => setOpen(false)} className={active} data-testid="mobile-nav-profile">My Profile</NavLink>}
            <div className="mt-3 flex gap-2">
              {!user ? (
                <>
                  <Button asChild variant="outline" className="flex-1" onClick={() => setOpen(false)}><Link to="/login">Login</Link></Button>
                  <Button asChild className="flex-1 bg-[#C85A32] hover:bg-[#a8482a] text-white" onClick={() => setOpen(false)}><Link to="/register">Sign up</Link></Button>
                </>
              ) : (
                <Button variant="outline" className="flex-1" onClick={() => { setOpen(false); doLogout(); }}>Logout</Button>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
